import React, { useCallback, useEffect, useState } from 'react';

import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    InlineGrid,
    InlineStack,
    Page,
    Select,
    Spinner,
    Text,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import AddCategoryExceptionModal from '../components/AddCategoryExceptionModal';
import CategoryMappingTable from '../components/CategoryMappingTable';
import useAdminApi from '../hooks/Api';

const EXCEPTION_MODAL_ID = 'category-exception-modal';
const PATH_SEPARATOR = ' › ';

/**
 * Deep taxonomy paths run to seven levels, which is far too long to read in a row.
 * Keep the vertical and the category itself — the two parts that identify it — and
 * elide everything between.
 */
function shortPath(path) {
    const parts = path.split(PATH_SEPARATOR);

    if (parts.length <= 3) return path;

    return [parts[0], '…', parts[parts.length - 1]].join(PATH_SEPARATOR);
}

function CategoryMappingPage() {
    const { request } = useAdminApi();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [countryId, setCountryId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [savingGid, setSavingGid] = useState(null);
    const [error, setError] = useState(null);

    const loadData = useCallback(
        async (targetCountryId) => {
            setIsLoading(true);

            const query = targetCountryId ? `?country_id=${targetCountryId}` : '';
            const { ok, data: fresh } = await request(`category-mappings${query}`);

            if (!ok) {
                setError('Could not load the category mapping right now. Reload to try again.');
                setIsLoading(false);
                return;
            }

            setData(fresh);
            setCountryId(fresh.country?.id ?? null);
            setError(null);
            setIsLoading(false);
        },
        [request],
    );

    useEffect(() => {
        loadData();
    }, []);

    /** An empty code clears the merchant's override and falls back to the shared default. */
    async function saveMapping(gid, code) {
        setSavingGid(gid);
        setError(null);

        const body = { country_id: countryId, shopify_category_gid: gid };

        const { ok, data: fresh } = code
            ? await request.put('category-mappings', { ...body, category_code: code })
            : await request.delete('category-mappings', body);

        if (!ok) {
            setError(fresh?.message ?? 'That change could not be saved.');
            setSavingGid(null);

            return false;
        }

        setData(fresh);
        setSavingGid(null);

        return true;
    }

    if (isLoading && !data) {
        return (
            <Page
                title="Category Mapping"
                backAction={{ content: 'Retail Locations', onAction: () => navigate('/locations') }}
            >
                <InlineStack align="center">
                    <Spinner accessibilityLabel="Loading category mapping" size="large" />
                </InlineStack>
            </Page>
        );
    }

    if (!data) {
        return (
            <Page
                title="Category Mapping"
                backAction={{ content: 'Retail Locations', onAction: () => navigate('/locations') }}
            >
                <Banner tone="critical">{error ?? 'No mapping is available.'}</Banner>
            </Page>
        );
    }

    if (!data.country) {
        const names = data.mappable_country_names ?? [];

        return (
            <Page
                title="Category Mapping"
                backAction={{ content: 'Retail Locations', onAction: () => navigate('/locations') }}
            >
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingMd" as="h2">
                            No locations need category mapping yet
                        </Text>
                        <Text as="p">
                            Planet requires an article code on every line item in{' '}
                            {names.length ? names.join(', ') : 'Spain, Portugal and France'}. None of your retail
                            locations are set up in those countries, so there is nothing to map here yet.
                        </Text>
                        <InlineStack>
                            <Button onClick={() => navigate('/locations')}>Go to Retail Locations</Button>
                        </InlineStack>
                    </BlockStack>
                </Card>
            </Page>
        );
    }

    const verticals = data.verticals.map((row) => ({ ...row, label: row.name }));
    const exceptions = data.exceptions.map((row) => ({ ...row, label: shortPath(row.path) }));

    return (
        <Page
            title="Category Mapping"
            backAction={{ content: 'Retail Locations', onAction: () => navigate('/locations') }}
            secondaryActions={
                data.countries.length > 1 ? (
                    <Box minWidth="180px">
                        <Select
                            label="Country"
                            labelHidden
                            options={data.countries.map((country) => ({
                                value: `${country.id}`,
                                label: country.name,
                            }))}
                            value={`${countryId}`}
                            onChange={(value) => loadData(value)}
                        />
                    </Box>
                ) : undefined
            }
        >
            <Box paddingBlockEnd="800">
                <BlockStack gap="400">
                    {error && (
                        <Banner tone="critical" onDismiss={() => setError(null)}>
                            {error}
                        </Banner>
                    )}

                    {/* Sits here rather than in Page subtitle: the country selector narrows the
                        header's title column, which squeezes this into a few words per line. */}
                    <Text tone="subdued" as="p">
                        Set the Planet article code each Shopify category reports. A code covers everything beneath that
                        category, and a code on an individual product always wins.
                    </Text>

                    <Card padding="0">
                        <CategoryMappingTable
                            rows={verticals}
                            codes={data.codes}
                            savingGid={savingGid}
                            onChange={saveMapping}
                            resourceName={{ singular: 'category', plural: 'categories' }}
                        />
                    </Card>

                    <Card padding="0">
                        <Box padding="400">
                            {/* A form control fills the inline size it is given, so in an InlineStack it
                                pushes its siblings onto their own row at any width. The heading and its
                                action get explicit grid columns instead. */}
                            <InlineGrid columns="1fr auto" gap="300" alignItems="center">
                                <Text variant="headingMd" as="h2">
                                    Sub-category exceptions
                                </Text>
                                <Button variant="primary" onClick={() => shopify.modal.show(EXCEPTION_MODAL_ID)}>
                                    Add exception
                                </Button>
                            </InlineGrid>
                        </Box>

                        {exceptions.length > 0 && (
                            <CategoryMappingTable
                                rows={exceptions}
                                codes={data.codes}
                                savingGid={savingGid}
                                onChange={saveMapping}
                                resourceName={{ singular: 'exception', plural: 'exceptions' }}
                            />
                        )}
                    </Card>
                </BlockStack>
            </Box>

            <AddCategoryExceptionModal id={EXCEPTION_MODAL_ID} codes={data.codes} onAdd={saveMapping} />
        </Page>
    );
}

export default CategoryMappingPage;
